import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../api/authFetch';
import bridgeService from '../api/bridgeService';
import './UserProfilePage.css';

const REDOWNLOAD_WINDOW_HOURS = 24;
const REDOWNLOAD_LIMIT = 4;

function UserProfilePage() {
  const { currentUser } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [redownloading, setRedownloading] = useState(null);
  const [redownloadError, setRedownloadError] = useState('');
  const [sending, setSending] = useState(null);
  const [transferResult, setTransferResult] = useState(null);

  useEffect(() => {
    if (currentUser) fetchPurchaseData();
  }, [currentUser]);

  const fetchPurchaseData = async () => {
    try {
      setIsLoading(true);
      const [purchasesResponse, summaryResponse] = await Promise.all([
        authFetch(`/api/mailvotes/payments/purchases/${currentUser.uid}`),
        authFetch(`/api/mailvotes/payments/summary/${currentUser.uid}`)
      ]);

      if (purchasesResponse.status === 503 || summaryResponse.status === 503) {
        setError('Payment system is currently unavailable. Purchase history cannot be displayed.');
        return;
      }

      const purchasesData = await purchasesResponse.json();
      const summaryData = await summaryResponse.json();

      if (purchasesData.success) {
        setPurchases(purchasesData.purchases);
      } else {
        throw new Error(purchasesData.message || 'Failed to fetch purchases');
      }

      if (summaryData.success) {
        setSummary(summaryData.summary);
      } else {
        throw new Error(summaryData.message || 'Failed to fetch summary');
      }
    } catch (err) {
      console.error('Error fetching purchase data:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRedownload = async (purchase) => {
    setRedownloading(purchase.id);
    setRedownloadError('');
    try {
      const res = await authFetch(`/api/mailvotes/downloads/redownload/${purchase.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setRedownloadError(data.error || 'Failed to regenerate download link');
        return;
      }
      // Open the download in a new tab
      window.open(data.downloadUrl, '_blank', 'noopener,noreferrer');

      // Update the counter locally so the UI reflects the new state immediately
      setPurchases(prev => prev.map(p =>
        p.id === purchase.id
          ? { ...p, download_count: data.downloadCount, last_downloaded_at: new Date().toISOString() }
          : p
      ));
    } catch (err) {
      console.error('Re-download failed:', err);
      setRedownloadError('Failed to regenerate download link');
    } finally {
      setRedownloading(null);
    }
  };

  const handleOpenInDoorNoc = (purchase) => {
    if (!purchase) return;
    setSending(purchase.id);
    setRedownloadError('');

    // Fire-and-forget audit bridge transfer; we never block on it.
    (async () => {
      try {
        const params = typeof purchase.search_parameters === 'string'
          ? JSON.parse(purchase.search_parameters || '{}')
          : (purchase.search_parameters || {});
        const state = (params.State || '').toString().toLowerCase() || 'ok';
        await bridgeService.sendToDoorNoc({ state, filters: params, purchaseId: purchase.id });
      } catch (_) { /* non-blocking */ }
    })();

    const target = `https://app.doornoc.com/routes/create?wizard=1&mailvotesPurchaseId=${purchase.id}`;
    try { window.sessionStorage.setItem('mv_pending_purchase_id', String(purchase.id)); } catch (_) {}
    window.location.assign(target);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatSearchFilters = (searchParams) => {
    if (!searchParams) return 'All voters';
    const filters = typeof searchParams === 'string' ? JSON.parse(searchParams) : searchParams;
    const filterPairs = Object.entries(filters)
      .filter(([, value]) => value && value.toString().trim() !== '')
      .map(([key, value]) => {
        const fieldName = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        return `${fieldName}: ${value}`;
      });
    return filterPairs.length > 0 ? filterPairs.join(', ') : 'All voters';
  };

  const formatPaymentMethod = (purchase) => {
    if (purchase.payment_method_last4 && purchase.payment_method_brand) {
      return `•••• ${purchase.payment_method_last4} (${purchase.payment_method_brand.toUpperCase()})`;
    }
    return 'Card payment';
  };

  // Re-download count is reset once the 24-hour window has elapsed since the
  // last download. We mirror that logic here to render the quota accurately.
  const remainingDownloads = (purchase) => {
    const last = purchase.last_downloaded_at ? new Date(purchase.last_downloaded_at).getTime() : 0;
    const windowMs = REDOWNLOAD_WINDOW_HOURS * 60 * 60 * 1000;
    const insideWindow = last && (Date.now() - last) < windowMs;
    const used = insideWindow ? (purchase.download_count || 0) : 0;
    return Math.max(0, REDOWNLOAD_LIMIT - used);
  };

  if (!currentUser) {
    return (
      <div className="user-profile-page">
        <div className="error-message">Please log in to view your profile.</div>
      </div>
    );
  }

  return (
    <div id="profileContent" className="content-section">
      <div className="user-info-section">
        <div className="user-info">
          <h3>{currentUser.displayName || currentUser.email}</h3>
          <p className="user-email">{currentUser.email}</p>
        </div>
      </div>

      {isLoading ? (
        <div className="loading-section">
          <div className="spinner"></div>
          <p>Loading purchase history...</p>
        </div>
      ) : error ? (
        <div className="error-message">Error: {error}</div>
      ) : (
        <>
          {summary && (
            <div className="summary-section">
              <h2>Purchase Summary</h2>
              <div className="summary-grid">
                <div className="summary-card">
                  <div className="summary-number">{summary.successful_purchases}</div>
                  <div className="summary-label">Total Purchases</div>
                </div>
                <div className="summary-card">
                  <div className="summary-number">${summary.total_spent}</div>
                  <div className="summary-label">Total Spent</div>
                </div>
              </div>
            </div>
          )}

          <div className="purchases-section">
            <h2>Purchase History</h2>
            <p style={{ color: '#666', fontSize: 14, margin: '0 0 16px' }}>
              Download each list up to {REDOWNLOAD_LIMIT} times per {REDOWNLOAD_WINDOW_HOURS} hours.
            </p>

            {redownloadError && (
              <div style={{
                padding: '10px 14px',
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: 8,
                marginBottom: 12,
                color: '#a51817',
                fontSize: 14
              }}>
                {redownloadError}
                <button
                  onClick={() => setRedownloadError('')}
                  style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  ×
                </button>
              </div>
            )}

            {purchases.length === 0 ? (
              <div className="no-purchases">
                <p>No purchases found.</p>
                <p>Make your first purchase by searching for voters and clicking "Purchase & Download CSV".</p>
              </div>
            ) : (
              <div className="purchases-table-container">
                <table className="purchases-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Search Filters</th>
                      <th>Payment Method</th>
                      <th>Status</th>
                      <th>Download</th>
                      <th>Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map((purchase) => {
                      const remaining = remainingDownloads(purchase);
                      const canDownload = purchase.status === 'succeeded' && remaining > 0;
                      return (
                        <tr key={purchase.id}>
                          <td>
                            <div className="date-column">
                              <div className="purchase-date">
                                {formatDate(purchase.completed_at || purchase.created_at)}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="amount-column">
                              <span className="amount">${purchase.amount_dollars}</span>
                              <span className="currency">{purchase.currency?.toUpperCase()}</span>
                            </div>
                          </td>
                          <td>
                            <div className="filters-column">
                              {formatSearchFilters(purchase.search_parameters)}
                            </div>
                          </td>
                          <td>
                            <div className="payment-method">{formatPaymentMethod(purchase)}</div>
                          </td>
                          <td>
                            <span className={`status-badge status-${purchase.status}`}>
                              {purchase.status.charAt(0).toUpperCase() + purchase.status.slice(1)}
                            </span>
                          </td>
                          <td>
                            {purchase.status !== 'succeeded' ? (
                              <span style={{ color: '#999', fontSize: 13 }}>—</span>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <button
                                  onClick={() => handleRedownload(purchase)}
                                  disabled={!canDownload || redownloading === purchase.id}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: canDownload ? '#17688e' : '#b0bec5',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 6,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    cursor: canDownload && redownloading !== purchase.id ? 'pointer' : 'not-allowed'
                                  }}
                                >
                                  {redownloading === purchase.id
                                    ? 'Generating...'
                                    : remaining === 0
                                      ? 'Limit reached'
                                      : 'Re-download CSV'}
                                </button>
                                <span style={{ fontSize: 11, color: '#666' }}>
                                  {remaining}/{REDOWNLOAD_LIMIT} left in {REDOWNLOAD_WINDOW_HOURS}h
                                </span>
                                <button
                                  onClick={() => handleOpenInDoorNoc(purchase)}
                                  disabled={sending === purchase.id}
                                  title="Send this list to your DoorNoc account for canvassing (requires a DoorNoc subscription)"
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: sending === purchase.id ? '#b0bec5' : '#2e7d32',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 6,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    cursor: sending === purchase.id ? 'not-allowed' : 'pointer',
                                    marginTop: 2
                                  }}
                                >
                                  {sending === purchase.id ? 'Opening…' : 'Open in DoorNoc'}
                                </button>
                              </div>
                            )}
                          </td>
                          <td>
                            {purchase.receipt_url ? (
                              <a
                                href={purchase.receipt_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="receipt-link"
                              >
                                View Receipt
                              </a>
                            ) : (
                              <span className="no-receipt">Receipt not available</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {transferResult && (
        <div
          onClick={() => setTransferResult(null)}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2000, padding: 20
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white', borderRadius: 16, maxWidth: 480,
              width: '100%', padding: 32, boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
          >
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              backgroundColor: '#e8f5e9', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              marginBottom: 20
            }}>
              <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#2e7d32" strokeWidth="3">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 style={{ margin: '0 0 8px', color: '#112030', fontSize: 22 }}>
              {transferResult.voterCount?.toLocaleString()} voters sent to DoorNoc!
            </h2>
            <p style={{ color: '#555', fontSize: 15, lineHeight: 1.5, margin: '0 0 20px' }}>
              Open DoorNoc, create a new campaign or route, and pick <strong>Import from MailVotes</strong> to use this list. The transfer expires in 24 hours.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setTransferResult(null)}
                style={{
                  padding: '10px 20px', backgroundColor: '#f5f5f5',
                  color: '#333', border: '1px solid #ccc', borderRadius: 8,
                  fontSize: 14, fontWeight: 500, cursor: 'pointer'
                }}
              >
                Close
              </button>
              <button
                onClick={() => {
                  window.open('https://app.doornoc.com/dashboard', '_blank', 'noopener,noreferrer');
                  setTransferResult(null);
                }}
                style={{
                  padding: '10px 20px', backgroundColor: '#17688e',
                  color: 'white', border: 'none', borderRadius: 8,
                  fontSize: 14, fontWeight: 600, cursor: 'pointer'
                }}
              >
                Open DoorNoc →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserProfilePage;
